import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  DetailsList,
  IColumn,
  IDetailsList,
  IGroup,
  mergeStyles,
  SelectionMode,
  Dialog,
  DialogType,
  DialogFooter,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  TooltipHost,
  Text,
} from '@fluentui/react';
import { CommandBarButton, DefaultButton, PrimaryButton } from '@fluentui/react';
import { AnalyticsActions } from './AnalyticsActions';
import { AnalyticsViews } from './AnalyticsModel';
import ErrorResponse from 'src/Models/ErrorResponse';
import { appInsights } from 'src/Services/Insights';
import { GetUserAppInsightProperties } from 'src/utils/AppInsightProperties';
import { UATConstants } from 'src/Common/Models/UATConstants';

const controlWrapperClass = mergeStyles({
  display: 'flex',
  flexWrap: 'wrap',
});

interface IDetailsListGroupedItem {
  id: string;
  name: string;
  description: string;
  visibility: string;
  viewType: string;
  createdBy: string | null;
  createdDate: string;
  lastModifiedBy: string | null;
  lastModifiedDate: string;
  links: {
    self: {
      href: string;
    };
  };
  url: string;
  starred: boolean;
}

interface Props {
  editCallBack: Function;
  reload: boolean;
  onShowMessage: Function;
}

export const AnalyticsViewList: React.FC<Props> = (props) => {
  const _root = useRef<IDetailsList>(null);
  const [items, setItems] = useState<IDetailsListGroupedItem[]>([]);

  const intgroups: IGroup[] = [
    {
      key: 'own',
      name: 'My Views',
      startIndex: 0,
      count: items.filter((item) => item.visibility === '0').length,
    },
    {
      key: 'shared',
      name: 'Shared Views',
      startIndex: items.filter((item) => item.visibility === '1').length,
      count: items.filter((item) => item.visibility === 'shared').length,
    },
  ];
  const [groups, setGroup] = useState<IGroup[]>(intgroups);

  const _columns: IColumn[] = [
    {
      key: 'name',
      name: 'Name',
      fieldName: 'name',
      minWidth: 100,
      maxWidth: 300,
      isResizable: true,
    },
    {
      key: 'description',
      name: 'Description',
      fieldName: 'description',
      minWidth: 100,
      maxWidth: 300,
      isResizable: true,
    },
    {
      key: 'lastmodifiedby',
      name: 'Last modified by',
      fieldName: 'lastModifiedBy',
      minWidth: 100,
      maxWidth: 300,
    },
    {
      key: 'actions',
      name: 'Action',
      onRender: (item) => (
        <CommandBarButton
          className='av-MenuItem'
          menuIconProps={{ iconName: 'More' }}
          menuProps={{
            items: [
              {
                key: 'edit',
                name: 'Edit',
                iconProps: { iconName: 'Edit' },
                onClick: () => handleEdit(item),
              },
              {
                key: 'delete',
                name: 'Delete',
                iconProps: { iconName: 'cancel' },
                onClick: () => handleDelete(item),
              },
            ],
          }}
        />
      ),
      minWidth: 10,
      maxWidth: 60,
    },
  ];

  useEffect(() => {
    if (items !== undefined) {
      setSpinner(true);
      AnalyticsActions.GetAnalyticsViewList(OnResultReceived);
    }
  }, []);

  const OnResultReceived = (data: AnalyticsViews[]) => {
    const rItems: IDetailsListGroupedItem[] = [];
    data.map((x) => {
      const createdby =
        x.createdBy?.displayName === undefined || null ? '' : x.createdBy?.displayName;
      rItems.push({
        id: x.id,
        name: x.name,
        description: x.description,
        visibility: x.visibility,
        viewType: x.viewType,
        createdBy: createdby,
        createdDate: x.createdDate === null ? '' : x.createdDate.toString(),
        lastModifiedBy:
          x.lastModifiedBy?.displayName === undefined || null
            ? createdby
            : x.lastModifiedBy?.displayName,
        lastModifiedDate: x.lastModifiedDate === null ? '' : x.lastModifiedDate.toString(),
        links: {
          self: {
            href: x._links.self.href,
          },
        },
        url: x.url,
        starred: true,
      });
    });
    setItems(rItems);
    setSpinner(false);
  };

  //before grouping sorted items list
  const sortedItems = (obj: any) => {
    return obj.sort((a: any, b: any) => {
      if (a.visibility === 0 && b.visibility === 1) {
        return -1;
      } else if (a.visibility === 1 && b.visibility === 0) {
        return 1;
      } else {
        return 0;
      }
    });
  };

  useEffect(() => {
    if (items !== undefined) {
      let tGroups: IGroup[] = [];
      const myViewItems = sortedItems(items).filter((item: any) => item.visibility === 0).length;
      const sharedViewItems = sortedItems(items).filter(
        (item: any) => item.visibility === 1
      ).length;
      tGroups = [
        {
          key: 'own',
          name: 'My Views',
          startIndex: 0,
          count: myViewItems,
        },
        {
          key: 'shared',
          name: 'Shared Views',
          startIndex: myViewItems,
          count: sharedViewItems,
        },
      ];
      setGroup(tGroups);
    }
  }, [items]);

  const handleEdit = (item: IDetailsListGroupedItem) => {
    // Handle edit action here
    props.editCallBack(item.id);
  };

  //dialog box show after delete click
  const handleDelete = (item: IDetailsListGroupedItem) => {
    setSelectedItem(item);
    setShowDialog(true);
  };

  // dialog box delete
  const handleDialogDel = () => {
    onDeleteSuccess(true);
    if (selectedItem)
      AnalyticsActions.DeleteAnalyticsView(selectedItem.id, onDeleteSuccess, onError);
  };

  const onDeleteSuccess = (response: boolean) => {
    setSelectedItem(undefined);
    setShowDialog(false);
    props.onShowMessage('View Deleted successfully');
    const items: IDetailsListGroupedItem[] = [];
    setItems(items);
    appInsights.trackEvent(
      {
        name: 'UAT-AnalyticsView-Deleted',
        properties: {
          viewName: selectedItem?.name,
          viewDeletedDate: new Date().toISOString(),
        },
      },
      GetUserAppInsightProperties()
    );
    AnalyticsActions.GetAnalyticsViewList(OnResultReceived);
  };

  const onError = (error: ErrorResponse) => {
    console.log(error);
  };

  const _onRenderColumn = useCallback(
    (item: IDetailsListGroupedItem, index?: number, column?: IColumn) => {
      const value =
        item && column && column.fieldName
          ? item[column.fieldName as keyof IDetailsListGroupedItem] || ''
          : '';

      let renderedValue: React.ReactNode = '';

      if (typeof value === 'object' && value !== null && 'self' in value && 'href' in value.self) {
        renderedValue = value.self.href;
      } else {
        renderedValue = String(value);
      }

      if (column && column.fieldName === 'description') {
        return (
          <TooltipHost content={String(renderedValue)}>
            <Text className='analyticsViewGridText'>{renderedValue}</Text>
          </TooltipHost>
        );
      }

      return <Text className='analyticsViewGridText'>{renderedValue}</Text>;
    },
    []
  );

  const [showDialog, setShowDialog] = useState(false);
  const [showSpinner, setSpinner] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<IDetailsListGroupedItem>();
  const [showMessage, setShowMessage] = useState<boolean>(false);

  return (
    <div className='createAnalyticsViewContent'>
      <div className={controlWrapperClass}>
        <Dialog
          hidden={!showDialog}
          onDismiss={() => setShowDialog(false)}
          dialogContentProps={{
            type: DialogType.normal,
            title: 'Delete view',
          }}
          modalProps={{
            isBlocking: false,
          }}>
          {selectedItem && (
            <div>
              <p>
                {UATConstants.ErrorMessages.Deleted}'{selectedItem.name}'
                {UATConstants.ErrorMessages.RemovedAllUsers}
              </p>
            </div>
          )}
          <DialogFooter>
            <PrimaryButton onClick={handleDialogDel} text='Yes' />
            <DefaultButton onClick={() => setShowDialog(false)} text='No' />
          </DialogFooter>
        </Dialog>
      </div>

      {showMessage && (
        <div className='av-messageHeaderMiddle'>
          <MessageBar
            messageBarType={MessageBarType.success}
            isMultiline={false}
            className='messageBar'>
            View deleted successfully
          </MessageBar>
        </div>
      )}
      <div>
        {showSpinner && (
          <Spinner
            size={SpinnerSize.medium}
            label={UATConstants.PlaceholdersAndTitles.Loading}
            ariaLive='assertive'
            className='loading-panel-full'
          />
        )}
      </div>
      <DetailsList
        componentRef={_root}
        items={items}
        groups={groups}
        columns={_columns}
        className='av-list'
        groupProps={{
          showEmptyGroups: false,
          headerProps: {
            styles: { title: { fontSize: 14, fontWeight: '500' } },
          },
        }}
        onRenderItemColumn={_onRenderColumn}
        selectionMode={SelectionMode.none}
      />
    </div>
  );
};

export default AnalyticsViewList;
